import React, { useState, useEffect } from 'react'
import {
  Box,
  Typography,
  Table,
  TableHead,
  TableRow,
  TableCell,
  TableBody,
  Paper,
  Button,
  IconButton,
  Modal,
} from '@mui/material'
import AddIcon from '@mui/icons-material/Add'
import EditIcon from '@mui/icons-material/Edit'
import DeleteIcon from '@mui/icons-material/Delete'
import { LiquidityAssetCTO } from '@shared/LiquidityAssetCTO'
import LiquidityAssetForm from './LiquidityAssetForm'
import { useOtChainState } from '@react/redux/store'
import { fetchLiquidityAssets, saveLiquidityAssets } from '@react/fetchers/LiquidityAssetFetchers'
import { otAssert } from '@shared/conversions'
import LogoUpload from '@react/components/LogoUpload'
import { AddressPill } from '@react/components/addresses/AddressPill'

const modalBoxSx = {
  position: 'absolute',
  top: '50%',
  left: '50%',
  transform: 'translate(-50%, -50%)',
  width: 500,
  bgcolor: 'background.paper',
  boxShadow: 24,
  borderRadius: 2,
  p: 4,
}

export default function AdminLiquidityAssets() {
  const {
    stageConfiguration: { restAPIKey, serverApiBaseUrl },
    cognitoUser,
  } = useOtChainState()
  const [assets, setAssets] = useState<LiquidityAssetCTO[]>([])
  const [_loading, setLoading] = useState(true)
  const [modalOpen, setModalOpen] = useState(false)
  const [editIndex, setEditIndex] = useState<number | null>(null)

  useEffect(() => {
    if (serverApiBaseUrl && restAPIKey) {
      fetchLiquidityAssets(serverApiBaseUrl, restAPIKey)
        .then(setAssets)
        .catch((err) => {
          console.error('Error fetching liquidity assets:', err)
        })
        .finally(() => setLoading(false))
    }
  }, [serverApiBaseUrl, restAPIKey])

  const handleAdd = () => {
    setEditIndex(null)
    setModalOpen(true)
  }

  const handleEdit = (idx: number) => {
    setEditIndex(idx)
    setModalOpen(true)
  }

  const handleDelete = (idx: number) => {
    const newAssets = assets.filter((_, i) => i !== idx)
    setAssets(newAssets)
    otAssert(cognitoUser, 'cognitoUser is not defined')
    saveLiquidityAssets(serverApiBaseUrl, restAPIKey, cognitoUser, newAssets)
  }

  const handleSave = async (item: LiquidityAssetCTO, idx: number | null): Promise<void> => {
    let newAssets: LiquidityAssetCTO[]
    if (idx === null) {
      newAssets = [...assets, item]
    } else {
      newAssets = assets.map((row, i) => (i === idx ? item : row))
    }
    otAssert(cognitoUser, 'cognitoUser is not defined')
    await saveLiquidityAssets(serverApiBaseUrl, restAPIKey, cognitoUser, newAssets)
    setAssets(newAssets)
    setModalOpen(false)
    setEditIndex(null)
  }

  const handleCancel = () => {
    setModalOpen(false)
    setEditIndex(null)
  }

  const initialFormValue: LiquidityAssetCTO =
    editIndex !== null
      ? assets[editIndex]
      : {
          isLytNative: false,
          uuid: '',
          chainId: 0,
          chain: '',
          address: '',
          depositAddress: '',
          reserveTransferAddress: '',
          symbol: '',
          name: '',
          decimals: 0,
          exchangeRate: 1,
          timestamp: Math.floor(Date.now() / 1000),
        }

  return (
    <Box sx={{ p: 4 }}>
      <Typography variant="h4" gutterBottom>
        Admin Liquidity Assets
      </Typography>
      <Button startIcon={<AddIcon />} variant="contained" sx={{ mb: 2 }} onClick={handleAdd}>
        Add Asset
      </Button>
      <Paper sx={{ width: '100%', overflow: 'auto', mt: 2 }}>
        <Table>
          <TableHead>
            <TableRow>
              <TableCell>Icon</TableCell>
              <TableCell>Chain ID</TableCell>
              <TableCell>Chain Name</TableCell>
              <TableCell>Address</TableCell>
              <TableCell>Deposit Address</TableCell>
              <TableCell>Reserve Transfer Address</TableCell>
              <TableCell>Symbol</TableCell>
              <TableCell>Name</TableCell>
              <TableCell>Decimals</TableCell>
              <TableCell>Exchange Rate</TableCell>
              <TableCell>Timestamp</TableCell>
              <TableCell>Actions</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {assets.map((asset, idx) => (
              <TableRow key={asset.address + asset.chainId + asset.timestamp}>
                <TableCell>
                  <LogoUpload size={50} addrOrId={asset.address}></LogoUpload>
                </TableCell>
                <TableCell>{asset.chainId}</TableCell>
                <TableCell>{asset.chain}</TableCell>
                <TableCell>
                  <AddressPill addr={asset.address} />
                </TableCell>
                <TableCell>
                  <AddressPill addr={asset.depositAddress} />
                </TableCell>
                <TableCell>
                  <AddressPill addr={asset.reserveTransferAddress} />
                </TableCell>
                <TableCell>{asset.symbol}</TableCell>
                <TableCell>{asset.name}</TableCell>
                <TableCell>{asset.decimals}</TableCell>
                <TableCell>{asset.exchangeRate}</TableCell>
                <TableCell>{new Date(asset.timestamp * 1000).toLocaleString()}</TableCell>
                <TableCell>
                  <IconButton onClick={() => handleEdit(idx)}>
                    <EditIcon />
                  </IconButton>
                  <IconButton
                    onClick={() => {
                      handleDelete(idx)
                    }}
                  >
                    <DeleteIcon />
                  </IconButton>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </Paper>
      <Modal open={modalOpen} onClose={handleCancel}>
        <Box sx={modalBoxSx}>
          <LiquidityAssetForm
            liquidityAsset={initialFormValue}
            onSave={(item) => handleSave(item, editIndex)}
            onCancel={handleCancel}
            editMode={editIndex !== null}
          />
        </Box>
      </Modal>
    </Box>
  )
}
